import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { NgbNavChangeEvent } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { finalize, map, merge, Observable, Subject, Subscription, tap } from 'rxjs';
import { AuthService } from 'src/app/auth/auth.service';
import { SessionService } from 'src/app/auth/session.service';
import { AppGlobalControlValues } from 'src/app/common/app.global.control.values';
import { unsubscribeAll } from 'src/app/common/app.helpers';
import AppSettings from 'src/app/common/app.settings';
import AppValues from 'src/app/common/app.values';
import { OperationInfoService } from 'src/app/common/long-operations/operation-info.service';
import ProjectLinks from 'src/app/common/project.links';
import { CommunitiesService } from 'src/app/components/community/communities.service';
import { PostsService } from 'src/app/components/posts/posts.service';
import { ProjectsService } from 'src/app/components/projects/projects.service';
import { ListMode } from 'src/app/components/shared/list-controller/list-controller';
import {
  initialPaginationState,
  initialResolver,
} from 'src/app/components/shared/social-initiative/social-initiative.helpers';
import { userReaction } from 'src/app/components/user-relation-requests/relation-request';
import { RelationRequestKnd } from 'src/app/components/user-relation-requests/relation-request.enum';
import {
  ConnectionRequest,
  RelationRequests,
  SuggestedUser,
} from 'src/app/components/user-relation-requests/relation-request.interface';
import {
  CONNECTION_SERVICE_TOKEN,
  DOCTOR_LINK_SERVICE_TOKEN,
  PATIENT_LINK_SERVICE_TOKEN,
} from 'src/app/components/user-relation-requests/relation-request.tokens';
import { ConnectionRequestService } from 'src/app/components/user-relation-requests/services/connection-request.service';
import { DoctorLinkRequestService } from 'src/app/components/user-relation-requests/services/doctor-link-request.service';
import { PatientLinkRequestService } from 'src/app/components/user-relation-requests/services/patient-link-request.service';
import { RelationRequestDialogContextService } from 'src/app/components/user-relation-requests/services/relation-request-dialog-context.service';
import { PaginationResolver } from 'src/app/interfaces/pagination.interface';
import {
  AccountStatus,
  Communities,
  ConnectionStatus,
  FullUser,
  Posts,
  Projects,
  RelationGeography,
} from 'src/app/swagger-api';
import { CardType } from 'src/app/views/cards/cards.types';
import {
  DistributionPreferencesData,
  DistributionPreferencesService,
} from '../../../../preferences/distribution-preferences/distribution-preferences.service';
import { calculateRelationsCount } from '../../../../user-relation-requests/services/relation-shared.service';
import { ManageProfileService } from '../../../manage-profile.service';
import { IndividualRoles, TabTypes } from '../../../profile.enum';
import { ViewProfileState } from '../view-profile.state';
import {
  ButtonState,
  connectionActionType,
  connectionButtonState,
  defaultBtnSate,
  doctorLinkActionType,
  doctorLinkButtonState,
  patientLinkActionType,
  patientLinkButtonState,
} from './regular-user-profile-helper';

@Component({
  selector: 'app-regular-user-profile',
  templateUrl: './regular-user-profile.component.html',
  styleUrls: ['./regular-user-profile.component.scss'],
  providers: [
    { provide: DOCTOR_LINK_SERVICE_TOKEN, useClass: DoctorLinkRequestService },
    { provide: PATIENT_LINK_SERVICE_TOKEN, useClass: PatientLinkRequestService },
    { provide: CONNECTION_SERVICE_TOKEN, useClass: ConnectionRequestService },
    RelationRequestDialogContextService,
  ],
})
export class RegularUserProfileComponent extends ViewProfileState implements OnDestroy, OnInit, OnChanges {
  public readonly CardType: typeof CardType = CardType;
  public readonly ConnectionStatus: typeof ConnectionStatus = ConnectionStatus;
  public readonly IndividualRoles: typeof IndividualRoles = IndividualRoles;

  public readonly DEFAULT_ENTITY_LIST = { items: [], total_count: 0 };

  @Input({ required: true }) public user: FullUser;
  @Input({ required: true }) public isMessageSent: boolean;
  @Input({ required: true }) public activeUserRoleTab: IndividualRoles;

  @Output() public sendMessage: EventEmitter<void> = new EventEmitter<void>();
  @Output() public userRelationStatusChanged: EventEmitter<void> = new EventEmitter<void>();

  public paginationResolver: PaginationResolver = initialResolver();
  public connectionsMode: string = ListMode.BROWSE;
  public communities: Communities = this.DEFAULT_ENTITY_LIST;
  public projects: Projects = this.DEFAULT_ENTITY_LIST;
  public posts: Posts = this.DEFAULT_ENTITY_LIST;

  public locationUser: string;
  public adminSession: boolean;
  public TabTypes: typeof TabTypes = TabTypes;
  public pendingDeactivation = false;
  public pendingReactivation = false;
  public is2AuthLevelPassed = false;

  public isConnectionsLoading$: Observable<boolean>;
  public connections$: Observable<ConnectionRequest[]>;

  public readonly ACTIVE_ACCOUNT_STATUS = AccountStatus.ACTIVE;
  public readonly DEACTIVATED_ACCOUNT_STATUS = AccountStatus.DEACTIVATED;

  public distributionText: string;
  public distributionTotal: number;

  private subscriptions$: Subscription[] = [];
  private currentTab = 'profile_communities';

  private tabTypes: { [key: string]: () => void } = {
    profile_communities: () => {
      this.communitiesService.fetchPersonalSpecificEntities(this.user.user_id, this.paginationResolver.state);
    },
    profile_projects: () => {
      this.projectsService.fetchPersonalSpecificEntities(this.user.user_id, this.paginationResolver.state);
    },
    profile_posts: () => {
      this.postsService.fetchPersonalSpecificEntities(this.user.user_id, this.paginationResolver.state);
    },
    profile_connections: () => {
      const { state } = this.paginationResolver;

      this.connectionService.loadRegularUserRelation(this.user.user_id, {
        ...state.pagination,
        limit: AppValues.PAGE_LIMIT,
      });
    },
    doctor_patients: () => {
      this.loadRelationGeography();
    },
  };

  private readonly projectsService = inject(ProjectsService);
  private readonly communitiesService = inject(CommunitiesService);
  private readonly sessionService = inject(SessionService);
  private readonly postsService = inject(PostsService);
  private readonly authService = inject(AuthService);
  private readonly operationInfoService = inject(OperationInfoService);
  private readonly manageProfileService = inject(ManageProfileService);
  private readonly relationRequestModalContext = inject(RelationRequestDialogContextService);
  private readonly distributionPreferencesService = inject(DistributionPreferencesService);
  private readonly connectionService = inject(CONNECTION_SERVICE_TOKEN);
  private readonly doctorLinkService = inject(DOCTOR_LINK_SERVICE_TOKEN);
  private readonly patientLinkService = inject(PATIENT_LINK_SERVICE_TOKEN);
  private readonly translateService = inject(TranslateService);

  public readonly relationGeographyFetched$: Subject<RelationGeography> = new Subject();

  public ngOnInit(): void {
    this.initPaginationResolver();
    this.subscribeToProjectsChanges();
    this.subscribeToPostsChanges();
    this.subscribeToCommunitiesChanges();
    this.subscribeOnRelationStatusChanges();
    this.subscribeOnRelationGeographyChanges();
    this.subscribeToIsAdminSession();
    this.check2AuthLvlPassed();
    this.isProfileUncompleted(this.user);

    this.isConnectionsLoading$ = this.connectionService.isLoading$;

    this.connections$ = this.connectionService.relationRequestsList$.pipe(
      tap(
        (result: RelationRequests<ConnectionRequest>) =>
          (this.paginationResolver.state.pagination.totalItemsCount = result.totalCount),
      ),
      map((result: RelationRequests<ConnectionRequest>) => result.items),
    );
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['user'] && !changes['user'].firstChange) {
      this.tabTypes[this.currentTab]();
    }
  }

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
  }

  public get connectionBtn(): ButtonState {
    return this.user && this.sessionService.user
      ? connectionButtonState(this.user, this.sessionService.user)
      : defaultBtnSate;
  }

  public get doctorLinkBtn(): ButtonState {
    return this.user && this.sessionService.user
      ? doctorLinkButtonState(this.user, this.sessionService.user)
      : defaultBtnSate;
  }

  public get patientLinkBtn(): ButtonState {
    return this.user && this.sessionService.user
      ? patientLinkButtonState(this.user, this.sessionService.user)
      : defaultBtnSate;
  }

  /**
   * @description Handles tabs switching logic expressed through further calling methods
   *              to cleaning up local data and initiating a new API request to receive
   *              data for new tab.
   * @param { NgbNavChangeEvent } { nextId } Payload of the change event fired right
   *                                         before the tab change.
   */
  public onTabClick({ nextId }: NgbNavChangeEvent<string>): void {
    if (this.tabTypes[nextId]) {
      this.paginationResolver.state = initialPaginationState();
      this.resetLocalData();
      this.tabTypes[nextId]();
      this.currentTab = nextId;
    }
  }

  /**
   * @description Provide paginationResolver with initial data.
   */
  public initPaginationResolver(): void {
    this.paginationResolver = {
      state: initialPaginationState(),
      onPageChanged: (page: number) => {
        this.resetLocalData(page);
        this.tabTypes[this.currentTab]();
      },
    };

    this.tabTypes[this.currentTab]();
  }

  public onSendMessage(): void {
    if (this.sessionService.is2AuthLvlPassed) {
      this.sendMessage.emit();
    } else {
      const previousPage = `${ProjectLinks.LINK_PROFILE_VIEW}/${this.user.user_id}`;
      localStorage.setItem(AppSettings.LOCALSTORE_KEY_CHAT_WITH_USERID, this.user.user_id);
      this.authService.showSendMessageRefuseModal(previousPage);
    }
  }

  public check2AuthLvlPassed(): void {
    const chatWithUserID: string | null = localStorage.getItem(AppSettings.LOCALSTORE_KEY_CHAT_WITH_USERID);

    if (this.sessionService.is2AuthLvlPassed && chatWithUserID === this.user.user_id) {
      this.sendMessage.emit();
      localStorage.removeItem(AppSettings.LOCALSTORE_KEY_CHAT_WITH_USERID);
    }
  }

  public deactivateAccount(): void {
    this.pendingDeactivation = true;

    this.subscriptions$.push(
      this.manageProfileService
        .confirmDeactivateAccount(this.user.user_id)
        .pipe(
          finalize(() => {
            this.pendingDeactivation = false;
          }),
        )
        .subscribe((dialogConfirmed: boolean) => {
          this.user = {
            ...this.user,
            account_status: dialogConfirmed ? AccountStatus.DEACTIVATED : AccountStatus.ACTIVE,
          };
        }),
    );
  }

  public reactivateAccount(): void {
    this.pendingReactivation = true;

    this.operationInfoService.setupOperation(
      {
        blockedUIControls: [{ elementId: AppGlobalControlValues.LOG_OUT_BUTTON }],
        successfulMessage: this.relationRequestModalContext.successfullRestored,
      },
      () =>
        this.manageProfileService
          .reactivateAccount(this.user.user_id)
          .pipe(
            finalize(() => {
              this.pendingReactivation = false;
            }),
          )
          .subscribe(() => {
            this.user = {
              ...this.user,
              account_status: AccountStatus.ACTIVE,
            };
          }),
    );
  }

  public manageConnection(): void {
    const user: SuggestedUser = userReaction(this.user);
    const actionType = connectionActionType(this.user, this.sessionService.user);

    if (actionType === 'Accept') {
      this.acceptConnection(user);
    } else if (actionType === 'Unlink') {
      this.removeFromMyConnections();
    } else if (actionType === 'Link') {
      this.createConnectionRequest();
    }
  }

  public manageDoctorLink(): void {
    const user: SuggestedUser = userReaction(this.user);
    const actionType = doctorLinkActionType(this.user, this.sessionService.user);

    if (actionType === 'Accept') {
      this.acceptDoctor(user);
    } else if (actionType === 'Unlink') {
      this.unlinkDoctor(user);
    } else if (actionType === 'Link') {
      this.linkDoctor(user);
    }
  }

  public managePatientLink(): void {
    const user: SuggestedUser = userReaction(this.user);
    const actionType = patientLinkActionType(this.user, this.sessionService.user);

    if (actionType === 'Accept') {
      this.acceptPatient(user);
    } else if (actionType === 'Unlink') {
      this.unlinkPatient(user);
    } else if (actionType === 'Link') {
      this.linkPatient(user);
    }
  }

  private loadRelationGeography(): void {
    this.distributionPreferencesService.getDistributionResult(this.user.user_id, false, this.user.doctor.preferences);
  }

  private createConnectionRequest(): void {
    this.relationRequestModalContext.modalContext = RelationRequestKnd.CONNECTION;
    this.operationInfoService.setupOperation(
      {
        spinner: 'button',
        button: { id: 'btn-manage-connections' },
        blockedUIControls: [{ elementId: AppGlobalControlValues.LOG_OUT_BUTTON }],
        successfulMessage: this.relationRequestModalContext.successfullSent,
      },
      () => {
        this.connectionService.createRelationRequest(userReaction(this.user));
      },
    );
  }

  private acceptConnection(user: SuggestedUser): void {
    this.relationRequestModalContext.modalContext = RelationRequestKnd.CONNECTION;
    this.operationInfoService.setupOperation(
      {
        spinner: 'button',
        button: { id: 'btn-manage-connections' },
        successfulMessage: this.relationRequestModalContext.successfullAcceptInvitation,
        blockedUIControls: [{ elementId: AppGlobalControlValues.LOG_OUT_BUTTON }],
      },
      () => {
        this.connectionService.acceptRelationByUser(user);
      },
    );
  }

  private removeFromMyConnections(): void {
    this.relationRequestModalContext.modalContext = RelationRequestKnd.CONNECTION;
    this.operationInfoService.setupOperation(
      {
        blockedUIControls: [{ elementId: AppGlobalControlValues.LOG_OUT_BUTTON }],
        successfulMessage: this.relationRequestModalContext.successfullRemoveAcceptedRequest,
        confirmationDialog: this.relationRequestModalContext.comfirmRemovalAcceptedRequest,
      },
      () => {
        this.connectionService.removeRelationByUser(userReaction(this.user));
      },
    );
  }

  private acceptDoctor(user: SuggestedUser): void {
    this.relationRequestModalContext.modalContext = RelationRequestKnd.DOCTOR_LINK;
    this.operationInfoService.setupOperation(
      {
        spinner: 'button',
        button: { id: 'btn-manage-doctor-link' },
        successfulMessage: this.relationRequestModalContext.successfullAcceptInvitation,
        blockedUIControls: [{ elementId: AppGlobalControlValues.LOG_OUT_BUTTON }],
      },
      () => {
        this.doctorLinkService.acceptRelationByUser(user);
      },
    );
  }

  private linkDoctor(user: SuggestedUser): void {
    this.relationRequestModalContext.modalContext = RelationRequestKnd.DOCTOR_LINK;
    this.operationInfoService.setupOperation(
      {
        spinner: 'button',
        button: { id: 'btn-manage-doctor-link' },
        successfulMessage: this.relationRequestModalContext.successfullSent,
        blockedUIControls: [{ elementId: AppGlobalControlValues.LOG_OUT_BUTTON }],
      },
      () => {
        this.doctorLinkService.createRelationRequest(user);
      },
    );
  }

  private unlinkDoctor(user: SuggestedUser): void {
    this.relationRequestModalContext.modalContext = RelationRequestKnd.DOCTOR_LINK;
    this.operationInfoService.setupOperation(
      {
        blockedUIControls: [{ elementId: AppGlobalControlValues.LOG_OUT_BUTTON }],
        successfulMessage: this.relationRequestModalContext.successfullRemoveAcceptedRequest,
        confirmationDialog: this.relationRequestModalContext.comfirmRemovalAcceptedRequest,
      },
      () => {
        this.doctorLinkService.removeRelationByUser(user);
      },
    );
  }

  private acceptPatient(user: SuggestedUser): void {
    this.relationRequestModalContext.modalContext = RelationRequestKnd.PATIENT_LINK;
    this.operationInfoService.setupOperation(
      {
        spinner: 'button',
        button: { id: 'btn-manage-patient-link' },
        successfulMessage: this.relationRequestModalContext.successfullAcceptInvitation,
        blockedUIControls: [{ elementId: AppGlobalControlValues.LOG_OUT_BUTTON }],
      },
      () => {
        this.patientLinkService.acceptRelationByUser(user);
      },
    );
  }

  private linkPatient(user: SuggestedUser): void {
    this.relationRequestModalContext.modalContext = RelationRequestKnd.PATIENT_LINK;
    this.operationInfoService.setupOperation(
      {
        spinner: 'button',
        button: { id: 'btn-manage-patient-link' },
        successfulMessage: this.relationRequestModalContext.successfullSent,
        blockedUIControls: [{ elementId: AppGlobalControlValues.LOG_OUT_BUTTON }],
      },
      () => {
        this.patientLinkService.createRelationRequest(user);
      },
    );
  }

  private unlinkPatient(user: SuggestedUser): void {
    this.relationRequestModalContext.modalContext = RelationRequestKnd.PATIENT_LINK;
    this.operationInfoService.setupOperation(
      {
        blockedUIControls: [{ elementId: AppGlobalControlValues.LOG_OUT_BUTTON }],
        successfulMessage: this.relationRequestModalContext.successfullRemoveAcceptedRequest,
        confirmationDialog: this.relationRequestModalContext.comfirmRemovalAcceptedRequest,
      },
      () => {
        this.patientLinkService.removeRelationByUser(user);
      },
    );
  }

  /**
   * @description Clears out fetched data once either tab or another page has been
   *              switched and proceeds with previously selected page.
   * @param {number} page New tab that has been selected.
   */
  private resetLocalData(page = 1): void {
    this.projects = this.DEFAULT_ENTITY_LIST;
    this.communities = this.DEFAULT_ENTITY_LIST;
    this.connectionService.resetDataState();

    this.paginationResolver.state.pagination.currentPage = page;
  }

  /**
   * @description Subscribes for the results of profile posts request once the
   *              corresponding tab is clicked.
   */

  private subscribeToPostsChanges(): void {
    this.subscriptions$.push(
      this.postsService.postsChanges.subscribe((posts: Posts) => {
        this.posts = posts;
        this.paginationResolver.state.pagination.totalItemsCount = posts.total_count;
      }),
    );
  }

  /**
   * @description Subscribes for the results of profile projects request once the
   *              corresponding tab is clicked.
   */
  private subscribeToProjectsChanges(): void {
    this.subscriptions$.push(
      this.projectsService.socialGroupChanges.subscribe((projects: Projects) => {
        this.projects = projects;
        this.paginationResolver.state.pagination.totalItemsCount = projects.total_count;
      }),
    );
  }

  /**
   * @description Subscribes for the results of profile communities request once the
   *              corresponding tab is clicked.
   */
  private subscribeToCommunitiesChanges(): void {
    this.subscriptions$.push(
      this.communitiesService.socialGroupChanges.subscribe((communities: Communities) => {
        this.communities = communities;
        this.paginationResolver.state.pagination.totalItemsCount = communities.total_count;
      }),
    );
  }

  private subscribeOnRelationStatusChanges(): void {
    this.subscriptions$.push(
      merge(
        this.doctorLinkService.relationStatusChanges$,
        this.patientLinkService.relationStatusChanges$,
        this.connectionService.relationStatusChanges$,
      )
        .pipe(
          tap(() => {
            this.userRelationStatusChanged.emit();
          }),
        )
        .subscribe(),
    );
  }

  private subscribeOnRelationGeographyChanges(): void {
    let totalCount: number;
    this.subscriptions$.push(
      this.distributionPreferencesService.preferencesData.subscribe(
        (distributionResult: DistributionPreferencesData) => {
          totalCount = distributionResult.total;
          this.distributionTotal = totalCount;
          this.relationGeographyFetched$.next(distributionResult.relationGeography);
          this.setRelationGeographyText(
            distributionResult.relationGeography,
            totalCount,
            distributionResult.selectedCountry,
          );
        },
      ),
    );
  }

  private setRelationGeographyText(
    relationGeography: RelationGeography,
    totalCount: number,
    selectedCountry?: string,
  ): void {
    if (!selectedCountry) {
      this.distributionText = this.translateService.instant('GEOGRAPHIC_DISTRIBUTION_BY_ALL_COUNTRIES', {
        patients_counts: totalCount,
      }) as string;
    } else {
      const relationCount = calculateRelationsCount(relationGeography);
      this.distributionText = this.translateService.instant('GEOGRAPHIC_DISTRIBUTION_BY_COUNTRY', {
        patients_counts: relationCount,
        percentage: Math.round((relationCount / totalCount) * 1000) / Math.pow(10, 1),
        selected_country: selectedCountry,
      }) as string;
    }
  }

  private subscribeToIsAdminSession(): void {
    this.adminSession = this.sessionService.isAdminSession();
    this.is2AuthLevelPassed = this.sessionService.is2AuthLvlPassed;
    this.subscriptions$.push(
      this.sessionService.sessionChanges.subscribe(() => {
        this.adminSession = this.sessionService.isAdminSession();
        this.is2AuthLevelPassed = this.sessionService.is2AuthLvlPassed;
      }),
    );
  }
}
